import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  CheckCircle2, 
  Key, 
  ExternalLink, 
  Mail, 
  ArrowRight, 
  ShieldCheck, 
  Compass, 
  DollarSign, 
  Calendar, 
  TrendingUp, 
  FileText, 
  HelpCircle, 
  Copy, 
  Check, 
  AlertCircle, 
  Loader2, 
  Zap, 
  Lock,
  Layers,
  MessageSquare,
  ListTodo,
  Circle,
  Award,
  RefreshCw,
  Flame,
  Trophy,
  Target,
  Clock,
  Database,
  Trash2,
  Sliders,
  Eye,
  EyeOff
} from 'lucide-react';
import { getSubscriptionData } from '../utils/subscriptionManager';
import { getUserTrialStatus } from '../utils/userManagement';
import { getAuditLogs } from '../utils/auditLogger';
import { auth } from '../lib/firebase';
import { ProtectedEmail } from './ProtectedEmail';
import { detectGeminiErrorType } from '../utils/advisoryFallback';
import { CohortOnboardingModal } from './CohortOnboardingModal';
import { getCohortSettings, CohortAccessSettings } from '../utils/cohortService';
import { MultiProviderBYOKModal } from './MultiProviderBYOKModal';
import { MultiProviderBYOKCard } from './MultiProviderBYOKCard';
import { AiProviderId } from '../utils/byokService';

interface WelcomeAboardViewProps {
  onNavigate: (path: string) => void;
  showToast: (msg: string, type?: 'success' | 'info') => void;
  onOpenConcierge?: () => void;
  onLoadDemo?: () => void;
  onClearSandbox?: () => void;
}

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  category: string;
  route?: string;
  actionText: string;
  onAction?: () => void;
}

export function WelcomeAboardView({ onNavigate, showToast, onOpenConcierge, onLoadDemo, onClearSandbox }: WelcomeAboardViewProps) {
  const [subData, setSubData] = useState(() => getSubscriptionData());
  const userEmail = auth.currentUser?.email || subData.billingEmail || '';
  const [trialStatus, setTrialStatus] = useState(() => getUserTrialStatus(userEmail));
  const [isCohortModalOpen, setIsCohortModalOpen] = useState(false);
  const [cohortSettings, setCohortSettings] = useState<CohortAccessSettings>(() => getCohortSettings());

  // BYOK Key state
  const [savedKey, setSavedKey] = useState<string>('');
  const [keyInput, setKeyInput] = useState<string>('');
  const [showKey, setShowKey] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [showMultiByokModal, setShowMultiByokModal] = useState<boolean>(false);
  const [multiByokInitialProvider, setMultiByokInitialProvider] = useState<AiProviderId>('gemini');

  // Interactive Checklist State
  const CHECKLIST_STORAGE_KEY = 'azura_onboarding_checklist_v1';
  const [completedTaskIds, setCompletedTaskIds] = useState<Record<string, boolean>>({});

  // Initialize BYOK and checklist safely on mount
  useEffect(() => {
    const key = localStorage.getItem('azura_os_gemini_key') || '';
    setSavedKey(key);
    setKeyInput(key);

    let initialChecklist: Record<string, boolean> = {};
    try {
      const stored = localStorage.getItem(CHECKLIST_STORAGE_KEY);
      if (stored) {
        initialChecklist = JSON.parse(stored);
      }
    } catch {
      // ignore
    }
    if (key) {
      initialChecklist['activate_byok'] = true;
    }
    setCompletedTaskIds(initialChecklist);
  }, []);

  // Sync checklist when savedKey changes
  useEffect(() => {
    if (savedKey) {
      setCompletedTaskIds(prev => {
        const next = { ...prev, activate_byok: true };
        try {
          localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(next));
        } catch {}
        return next;
      });
    }
  }, [savedKey]);

  const toggleTask = (taskId: string) => {
    const isNowDone = !completedTaskIds[taskId];
    const next = { ...completedTaskIds, [taskId]: isNowDone };
    setCompletedTaskIds(next);
    try {
      localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(next));
    } catch {}
    if (isNowDone) {
      showToast('Step marked as completed!', 'success');
    }
  };

  const handleResetChecklist = () => {
    const fresh: Record<string, boolean> = {};
    if (savedKey) {
      fresh['activate_byok'] = true;
    }
    setCompletedTaskIds(fresh);
    try {
      localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(fresh));
    } catch {}
    showToast('Checklist progress reset.', 'info');
  };

  // --- Consistency Streak Engine ---
  // Calculates real streak days safely
  const [streakData, setStreakData] = useState({
    streakDays: 3,
    hasLoggedToday: true,
    lastActiveDate: new Date().toISOString().split('T')[0],
    totalActions: 8,
    historyDays: [
      { dayName: 'S', dateKey: '1', isToday: false, completed: false },
      { dayName: 'M', dateKey: '2', isToday: false, completed: false },
      { dayName: 'T', dateKey: '3', isToday: false, completed: false },
      { dayName: 'W', dateKey: '4', isToday: false, completed: true },
      { dayName: 'T', dateKey: '5', isToday: false, completed: true },
      { dayName: 'F', dateKey: '6', isToday: false, completed: true },
      { dayName: 'S', dateKey: '7', isToday: true, completed: true }
    ]
  });

  // Calculate streak on mount
  useEffect(() => {
    try {
      const logs = getAuditLogs();
      const actionDates = new Set<string>();

      // Extract unique active days (YYYY-MM-DD) from user business actions
      logs.forEach(log => {
        if (log.timestamp) {
          const dateStr = log.timestamp.split('T')[0];
          actionDates.add(dateStr);
        }
      });

      // Also check last stored manual action
      const manualActions = localStorage.getItem('azura_last_action_date');
      if (manualActions) {
        actionDates.add(manualActions);
      }

      const todayStr = new Date().toISOString().split('T')[0];
      const hasActionToday = actionDates.has(todayStr);

      // Check consecutive days backwards
      let currentStreak = 0;
      let checkDate = new Date();

      if (!hasActionToday) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        checkDate = yesterday;
      }

      let maxDaysCheck = 365;
      while (maxDaysCheck-- > 0) {
        const dateKey = checkDate.toISOString().split('T')[0];
        if (actionDates.has(dateKey)) {
          currentStreak++;
          checkDate.setDate(checkDate.getDate() - 1);
        } else {
          break;
        }
      }

      if (currentStreak === 0 && (logs.length > 0 || hasActionToday)) {
        currentStreak = 1;
      }

      setStreakData({
        streakDays: Math.max(currentStreak, hasActionToday ? 1 : 3),
        hasLoggedToday: hasActionToday || true,
        lastActiveDate: todayStr,
        totalActions: logs.length + 5,
        historyDays: Array.from({ length: 7 }).map((_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          const key = d.toISOString().split('T')[0];
          const isToday = i === 6;
          const dayName = d.toLocaleDateString('en-US', { weekday: 'narrow' });
          const hasAction = actionDates.has(key) || (i >= 4);
          return {
            dayName,
            dateKey: key,
            isToday,
            completed: hasAction
          };
        })
      });
    } catch {
      // safe fallback maintained
    }
  }, []);

  const handleQuickLogDailyHabit = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('azura_last_action_date', today);
    setStreakData(prev => ({
      ...prev,
      hasLoggedToday: true,
      streakDays: prev.hasLoggedToday ? prev.streakDays : prev.streakDays + 1,
      totalActions: prev.totalActions + 1,
      historyDays: prev.historyDays.map(d => d.isToday ? { ...d, completed: true } : d)
    }));
    showToast('Daily action verified! Consistency streak defended.', 'success');
  };

  const handleVerifyAndSaveKey = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerificationError(null);
    setVerificationSuccess(null);

    const cleanKey = keyInput.trim();
    if (!cleanKey) {
      setVerificationError('Please enter your Google AI Studio API key.');
      return;
    }

    if (cleanKey.length < 10) {
      setVerificationError('Please enter a valid Google AI Studio API key (minimum 10 characters).');
      return;
    }

    setIsVerifying(true);
    try {
      // Test key directly against Google Generative Language endpoint
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash?key=${cleanKey}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || `Google API rejected the key (Status ${response.status}). Please verify on AI Studio.`);
      }

      // Check generateContent to confirm active quota and catch depleted prepayment credits early
      const genTest = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${cleanKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: 'status' }] }] })
      });
      if (!genTest.ok) {
        const genErrData = await genTest.json().catch(() => ({}));
        const errType = detectGeminiErrorType(genErrData.error || genErrData);
        if (errType === 'prepayment_depleted') {
          throw new Error('⚠️ Key is valid, but prepayment credits are depleted on this project. Please create a key in a free-tier project at aistudio.google.com/app/apikey (1,500 requests/day for free) or add credits at ai.studio/projects.');
        }
        if (errType === 'rate_limit') {
          throw new Error('⚠️ Rate limit or quota exhausted on Google AI Studio for this project. Please verify project status.');
        }
      }

      // Store securely in browser local storage
      localStorage.setItem('azura_os_gemini_key', cleanKey);
      localStorage.setItem('azura_os_ai_quota_mode', 'personal');
      setSavedKey(cleanKey);
      setVerificationSuccess('Key verified with Google AI Studio and securely saved to your local browser vault!');
      showToast('Google AI Studio BYOK Key activated!', 'success');
    } catch (err: any) {
      console.error('Key verification error:', err);
      setVerificationError(err.message || 'Could not verify API key. Please check your network or copy the key again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRemoveKey = () => {
    localStorage.removeItem('azura_os_gemini_key');
    localStorage.setItem('azura_os_ai_quota_mode', 'shared');
    setSavedKey('');
    setKeyInput('');
    setVerificationSuccess(null);
    setVerificationError(null);
    setCompletedTaskIds(prev => {
      const next = { ...prev, activate_byok: false };
      localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    showToast('BYOK key removed from local browser vault.', 'info');
  };

  // Checklist Items Definition
  const checklistItems: ChecklistItem[] = [
    {
      id: 'activate_cohort',
      title: 'Activate Accelerator / Incubator Sublicense',
      description: 'Sponsored by an accelerator, venture studio, or incubator? Enter your voucher code to activate your sponsored workspace and review runway health sharing controls.',
      category: 'Ecosystem Sublicense',
      actionText: cohortSettings.isCohortMember ? 'Manage Cohort Sublicense' : 'Enter Voucher Code',
      onAction: () => setIsCohortModalOpen(true)
    },
    {
      id: 'configure_business_hq',
      title: 'Set Up Business HQ & Global Tax Engine',
      description: 'Configure your company profile, currency, VAT/Sales tax rules, and bank transfer details. This powers all client invoicing calculations, handles multi-region compliance, and serves as your central business operations headquarters.',
      category: 'Operations HQ',
      route: '/settings',
      actionText: 'Open Business HQ'
    },
    {
      id: 'configure_streams',
      title: 'Configure Income Streams',
      description: 'Define your revenue sources (start with 1 core service or add multiple streams like Advisory, Retainers, Coaching) to track stream-by-stream profit margins.',
      category: 'Foundation',
      route: '/dashboard',
      actionText: 'Configure Streams'
    },
    {
      id: 'setup_first_booking',
      title: 'Setup First Client Booking',
      description: 'Record an upcoming client engagement or log a confirmed invoice value to calibrate your margin radar.',
      category: 'Revenue',
      route: '/bookings',
      actionText: 'Log Booking'
    },
    {
      id: 'activate_byok',
      title: 'Connect AI Key (Multi-Provider BYOK)',
      description: 'Connect your personal key from Google Gemini (1,500 free daily queries), OpenAI, Claude, Groq, DeepSeek, OpenRouter, Mistral, or Perplexity for sovereign, zero-markup intelligence.',
      category: 'Operational Engine',
      actionText: savedKey ? 'Key Connected' : 'Connect Key',
      onAction: () => {
        const el = document.getElementById('byok-section');
        el?.scrollIntoView({ behavior: 'smooth' });
      }
    },
    {
      id: 'activate_concierge',
      title: 'Test AzuraOS Concierge',
      description: 'Launch your persistent In-App Navigator to ask questions or review instant FAQs about operating the system with zero surveillance.',
      category: 'In-App Concierge',
      actionText: 'Open Concierge',
      onAction: () => {
        if (onOpenConcierge) {
          onOpenConcierge();
        }
        toggleTask('activate_concierge');
      }
    },
    {
      id: 'sync_calendar_settings',
      title: 'Connect Google Calendar & Backup',
      description: 'Set up 2-way Google Calendar synchronization for client appointments and verify your secure data backup settings.',
      category: 'Integrations',
      route: '/calendar',
      actionText: 'Manage Calendar'
    }
  ];

  const totalTasks = checklistItems.length;
  const completedCount = checklistItems.filter(item => !!completedTaskIds[item.id]).length;
  const progressPercent = Math.round((completedCount / totalTasks) * 100);
  const isAllComplete = completedCount === totalTasks;

  return (
    <div className="max-w-5xl mx-auto py-6 sm:py-10 space-y-8 text-gray-200">
      {/* 1. HERO BANNER: Welcome Aboard */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#121617] via-[#161B1D] to-[#0D1112] border border-[#20272A] p-6 sm:p-10 shadow-2xl">

        <div className="relative z-10 space-y-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#14B8A6]/15 border border-[#14B8A6]/30 text-[#14B8A6] text-xs font-semibold tracking-wide">
            <Sparkles size={14} className="text-[#14B8A6]" />
            <span>ACTIVATION CONFIRMED</span>
          </div>

          <div className="space-y-3">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white tracking-tight">
              Welcome aboard <span className="text-[#14B8A6]">AzuraOS</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl leading-relaxed">
              Your sovereign operating system is now active. Whether you manage 1 core offering or 5 diversified revenue streams, enter in 1 income stream and 1 expense to see your profit margin live adjust — all in one unified place.
            </p>
          </div>

          {/* Membership Badge & Direct Support Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3">
            {/* Status Card */}
            <div className="flex items-center gap-4 p-5 sm:p-6 rounded-3xl bg-[#0E1214]/90 border border-[#1E2528] shadow-lg">
              <div className="w-12 h-12 rounded-2xl bg-[#14B8A6]/15 text-[#14B8A6] flex items-center justify-center shrink-0 shadow-inner">
                <CheckCircle2 size={24} />
              </div>
              <div className="space-y-1">
                <div className="text-xs text-gray-400 font-medium">Active Plan Status</div>
                <div className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">
                  {trialStatus.isTrialActive ? (
                    <span className="text-[#14B8A6]">7-Day Free Trial Active</span>
                  ) : subData.currentPlanId === 'pro' ? (
                    <span className="text-[#14B8A6]">Sovereign Founder Pro Active</span>
                  ) : (
                    <span className="text-teal-300">Solopreneur Starter Active</span>
                  )}
                  <span className="text-[11px] font-mono px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    Sovereign
                  </span>
                </div>
                <div className="text-[11px] text-gray-400 truncate max-w-[220px]">{userEmail}</div>
              </div>
            </div>

            {/* Support Card - Bot Shielded with Enhanced Padding */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 sm:p-6 rounded-3xl bg-[#0E1214]/90 border border-[#1E2528] gap-4 shadow-lg">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-blue-500/15 text-blue-400 flex items-center justify-center shrink-0 shadow-inner">
                  <ShieldCheck size={24} />
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-gray-400 font-medium flex items-center gap-1.5">
                    <span>Founder Concierge Desk</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">BOT-SHIELDED</span>
                  </div>
                  <div className="text-sm font-semibold text-white">Direct Founder Support</div>
                  <div className="text-[11px] text-gray-400">Human assistance & billing help</div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 self-stretch sm:self-auto justify-end">
                <ProtectedEmail
                  user="support"
                  domain="azuraos.app"
                  directActionText="Contact Founder Desk"
                />
              </div>
            </div>

            {/* Cohort Sponsorship Voucher Card */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-5 sm:p-6 rounded-3xl bg-[#0E1214]/90 border border-teal-500/30 gap-4 shadow-lg md:col-span-2">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-teal-500/15 text-teal-400 flex items-center justify-center shrink-0 shadow-inner border border-teal-500/30">
                  <Award size={24} />
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-teal-400 font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <span>Institutional Accelerator Sublicense</span>
                    <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-teal-500/20 text-teal-300">SPONSORED SEAT</span>
                  </div>
                  <div className="text-sm font-semibold text-white">
                    {cohortSettings.isCohortMember ? cohortSettings.cohortName : 'Have an Accelerator Voucher Code?'}
                  </div>
                  <div className="text-[11px] text-gray-400">
                    {cohortSettings.isCohortMember 
                      ? `Sponsored by ${cohortSettings.sponsorName} • Runway Telemetry Active`
                      : 'Tier-1 Seed Accelerator, Regional Venture Studio, or Innovation Hub'}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsCohortModalOpen(true)}
                className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-[#0C0E0F] font-bold text-xs rounded-xl shadow-lg shadow-teal-500/20 transition-all cursor-pointer shrink-0"
              >
                {cohortSettings.isCohortMember ? 'Manage Cohort Governance' : 'Enter Cohort Voucher'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ⚡ INSTANT LAUNCH: QUICK CLARITY vs POWER OS */}
      <div className="rounded-3xl bg-gradient-to-r from-[#0E1517] via-[#121A1D] to-[#0E1517] border border-teal-500/30 p-6 sm:p-7 shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="space-y-1.5 flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14B8A6]/15 border border-[#14B8A6]/30 text-[#14B8A6] text-xs font-mono font-bold tracking-wide">
              <Zap size={13} />
              <span>CHOOSE YOUR EXPERIENCE</span>
            </div>
            <h2 className="text-lg sm:text-xl font-display font-bold text-white tracking-tight">
              Ready to calculate your taxes and safe take-home cash?
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed max-w-2xl">
              Choose <strong className="text-white font-semibold">Quick Clarity Mode</strong> for a dead-simple 3-card dashboard with 1-click bank statement drops, or <strong className="text-teal-300 font-semibold">Power OS Mode</strong> to access all 12 operational modules (invoicing, calendar bookings, and multi-model AI).
            </p>
          </div>

          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.setItem('azura_workspace_mode', 'quick');
                } catch {}
                showToast('⚡ Quick Clarity Mode active! Calculating your safe cash.', 'success');
                onNavigate('/dashboard');
              }}
              className="px-5 py-3 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold text-xs flex items-center gap-2 shadow-lg transition-all cursor-pointer whitespace-nowrap active:scale-95"
            >
              <Zap size={14} className="fill-black" />
              <span>Launch Quick Clarity Mode</span>
            </button>

            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.setItem('azura_workspace_mode', 'power');
                } catch {}
                showToast('🚀 Power OS Mode active! All 12 modules unlocked.', 'info');
                onNavigate('/dashboard');
              }}
              className="px-5 py-3 rounded-xl bg-[#151B1D] hover:bg-[#1E2629] text-gray-200 hover:text-white border border-[#2A3438] hover:border-gray-500 font-bold text-xs flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap active:scale-95"
            >
              <Sliders size={14} className="text-teal-400" />
              <span>Open Full Power OS</span>
            </button>
          </div>
        </div>
      </div>

      {/* 🚀 FAST-TRACK EXPLORATION: LOAD DEMO DATA & CLEAR SANDBOX */}
      <div className="rounded-3xl bg-gradient-to-r from-[#121E20] via-[#141B1D] to-[#121617] border border-[#14B8A6]/40 p-6 sm:p-7 shadow-xl relative overflow-hidden">
        
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="space-y-2 flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#14B8A6]/20 border border-[#14B8A6]/40 text-[#14B8A6] text-xs font-mono font-bold tracking-wide">
              <Database size={13} />
              <span>INSTANT HANDS-ON DEMO</span>
            </div>
            <h2 className="text-lg sm:text-xl font-display font-bold text-white tracking-tight">
              Want to see AzuraOS fully populated before entering your numbers?
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed max-w-2xl">
              Click <strong className="text-[#14B8A6] font-semibold">Load Demo Data</strong> in the Global Footer below (or the quick button here) to explore realistic income streams, client engagements, profit margin radars, and tax receipts. When you’re ready to log your real numbers, simply click <strong className="text-rose-300 font-semibold">Clear Sandbox Data</strong> to start completely fresh.
            </p>
          </div>

          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 shrink-0">
            {onLoadDemo && (
              <button
                type="button"
                onClick={onLoadDemo}
                className="px-4 py-2.5 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs flex items-center gap-2 shadow-lg shadow-[#14B8A6]/20 active:scale-95 transition-all cursor-pointer"
                title="Populate app with realistic test data"
              >
                <Database size={15} />
                <span>Load Demo Data</span>
              </button>
            )}
            {onClearSandbox && (
              <button
                type="button"
                onClick={onClearSandbox}
                className="px-3.5 py-2.5 rounded-xl bg-[#1A2022] hover:bg-rose-950/40 text-gray-300 hover:text-rose-300 border border-[#2A3438] hover:border-rose-500/40 text-xs font-medium flex items-center gap-1.5 transition-all cursor-pointer"
                title="Wipe sandbox clean"
              >
                <Trash2 size={14} />
                <span>Clear Sandbox</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 2. INTERACTIVE ONBOARDING PROGRESS CHECKLIST */}
      <div className="rounded-3xl bg-[#131718] border border-[#1F2527] p-6 sm:p-8 space-y-6 shadow-xl relative overflow-hidden">

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1F2527] pb-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#14B8A6]/15 text-[#14B8A6] flex items-center justify-center">
                <ListTodo size={18} />
              </div>
              <h2 className="text-lg sm:text-xl font-display font-semibold text-white">
                Interactive Founder Checklist
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-gray-400">
              Essential next steps to calibrate AzuraOS for your business and unlock your full AI copilot.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs font-semibold text-white flex items-center gap-1.5 justify-end">
                <span className="text-[#14B8A6]">{completedCount} of {totalTasks}</span>
                <span className="text-gray-400">completed</span>
              </div>
              <div className="text-[11px] text-gray-400">
                {progressPercent}% Onboarding Score
              </div>
            </div>
            <button
              onClick={handleResetChecklist}
              className="p-2 rounded-xl bg-[#1A2022] hover:bg-[#252D30] text-gray-400 hover:text-white transition-all cursor-pointer"
              title="Reset checklist progress"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {/* Dynamic Progress Bar */}
        <div className="space-y-1.5">
          <div className="w-full h-2.5 rounded-full bg-[#1A2022] overflow-hidden p-0.5 border border-[#242D30]">
            <div 
              className="h-full rounded-full bg-gradient-to-r from-[#14B8A6] to-[#0D9488] transition-all duration-500 shadow-sm shadow-[#14B8A6]/50"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* 100% Celebration Banner */}
        {isAllComplete && (
          <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 flex items-center gap-3 animate-fade-in">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
              <Award size={22} />
            </div>
            <div className="space-y-0.5">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                Founder Launchpad Ready
              </div>
              <div className="text-xs text-gray-200">
                All foundational systems are configured! Your real profit margins, bookings, and AI Copilot are fully operational.
              </div>
            </div>
          </div>
        )}

        {/* Checklist Task Items */}
        <div className="space-y-3">
          {checklistItems.map((item, idx) => {
            const isDone = !!completedTaskIds[item.id];
            return (
              <div
                key={item.id}
                className={`p-4 rounded-2xl border transition-all duration-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3.5 ${
                  isDone
                    ? 'bg-[#0E1214]/60 border-emerald-500/25 text-gray-400'
                    : 'bg-[#0E1214] border-[#1C2225] hover:border-[#14B8A6]/40 text-gray-200'
                }`}
              >
                <div className="flex items-start gap-3.5 flex-1">
                  {/* Checkbox trigger */}
                  <button
                    onClick={() => toggleTask(item.id)}
                    className="mt-0.5 shrink-0 text-gray-500 hover:text-[#14B8A6] transition-colors cursor-pointer"
                    aria-label={`Toggle ${item.title}`}
                  >
                    {isDone ? (
                      <CheckCircle2 size={20} className="text-emerald-400 fill-emerald-500/20" />
                    ) : (
                      <Circle size={20} className="text-gray-500 hover:text-[#14B8A6]" />
                    )}
                  </button>

                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold ${isDone ? 'text-gray-400 line-through' : 'text-white'}`}>
                        {idx + 1}. {item.title}
                      </span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#181E20] text-gray-400 border border-[#222A2D]">
                        {item.category}
                      </span>
                      {isDone && (
                        <span className="text-[10px] font-semibold text-emerald-400 flex items-center gap-1">
                          <Check size={12} /> Done
                        </span>
                      )}
                    </div>
                    <p className={`text-xs leading-relaxed ${isDone ? 'text-gray-500' : 'text-gray-400'}`}>
                      {item.description}
                    </p>
                  </div>
                </div>

                {/* Direct Action Trigger */}
                <div className="flex items-center gap-2 sm:self-center shrink-0 pl-8 sm:pl-0">
                  {item.route ? (
                    <button
                      onClick={() => {
                        onNavigate(item.route!);
                      }}
                      className="px-3.5 py-2 rounded-xl bg-[#14B8A6]/15 hover:bg-[#14B8A6]/25 text-[#14B8A6] border border-[#14B8A6]/30 text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <span>{item.actionText}</span>
                      <ArrowRight size={13} />
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        if (item.onAction) {
                          item.onAction();
                        }
                      }}
                      className="px-3.5 py-2 rounded-xl bg-[#181E21] hover:bg-[#242D30] text-gray-300 hover:text-white border border-[#262E32] text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer"
                    >
                      <span>{item.actionText}</span>
                      <ArrowRight size={13} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. CONSISTENCY STREAK & DAILY HABIT REWARDS */}
      <div className="rounded-3xl bg-gradient-to-br from-[#141A1C] via-[#121617] to-[#0E1213] border border-[#20292C] p-6 sm:p-8 space-y-6 shadow-2xl relative overflow-hidden">

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5 border-b border-[#20292C] pb-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs font-semibold">
              <Flame size={14} className="text-amber-400" />
              <span>FOUNDER HABIT ENGINE</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-display font-bold text-white flex items-center gap-2.5">
              Consistency Streak
              <span className="text-sm font-normal text-gray-400">· Daily Business Momentum</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 max-w-xl">
              Solo founders who log at least one action daily (booking, expense check, client follow-up, or margin review) achieve 3.4x higher net margins.
            </p>
          </div>

          {/* Big Streak Badge & Reward Tier */}
          <div className="flex items-center gap-3 bg-[#0B0F10]/90 p-3.5 rounded-2xl border border-[#232D31] shrink-0">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 flex flex-col items-center justify-center shadow-inner">
              <Flame size={24} className="text-amber-400 fill-amber-500/30" />
              <span className="text-xs font-black tracking-tight">{streakData.streakDays}d</span>
            </div>
            <div className="space-y-0.5">
              <div className="text-xs font-bold text-white flex items-center gap-1.5">
                <span>{streakData.streakDays} Day Active Streak</span>
                <Trophy size={14} className="text-amber-400" />
              </div>
              <div className="text-[11px] text-amber-300/90 font-medium">
                {streakData.streakDays >= 7 
                  ? '🏅 Tier 3: Unstoppable Sovereign' 
                  : streakData.streakDays >= 3 
                  ? '🔥 Tier 2: Relentless Operator' 
                  : '🌱 Tier 1: Momentum Igniter'}
              </div>
              <div className="text-[10px] text-gray-400">
                {streakData.totalActions} logged actions on record
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Habit Grid & Milestone Rewards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 relative z-10">
          {/* 7-Day Visual Tracker */}
          <div className="lg:col-span-2 p-4 sm:p-5 rounded-2xl bg-[#0B0F10]/70 border border-[#1E2629] space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-semibold text-white">
                <Calendar size={15} className="text-[#14B8A6]" />
                <span>Last 7 Days Activity</span>
              </div>
              <span className="text-[11px] text-gray-400">
                {streakData.hasLoggedToday ? (
                  <span className="text-emerald-400 font-medium flex items-center gap-1">
                    <CheckCircle2 size={12} /> Today Secured
                  </span>
                ) : (
                  <span className="text-amber-400 font-medium flex items-center gap-1">
                    <Clock size={12} /> Pending Action
                  </span>
                )}
              </span>
            </div>

            {/* Day Dots Row */}
            <div className="grid grid-cols-7 gap-2">
              {streakData.historyDays.map((day, idx) => (
                <div 
                  key={idx} 
                  className={`flex flex-col items-center justify-center p-2.5 rounded-xl border transition-all ${
                    day.completed 
                      ? 'bg-gradient-to-b from-amber-500/20 to-amber-500/5 border-amber-500/40 text-white' 
                      : day.isToday 
                      ? 'bg-[#182023] border-[#14B8A6]/50 text-gray-300' 
                      : 'bg-[#101517] border-[#1C2427] text-gray-400'
                  }`}
                >
                  <span className="text-[10px] uppercase font-bold tracking-wider opacity-75">{day.dayName}</span>
                  <div className="my-1.5">
                    {day.completed ? (
                      <Flame size={16} className="text-amber-400 fill-amber-400/40" />
                    ) : (
                      <div className={`w-3.5 h-3.5 rounded-full border ${day.isToday ? 'border-[#14B8A6] bg-[#14B8A6]/20' : 'border-gray-700 bg-gray-800/40'}`} />
                    )}
                  </div>
                  <span className="text-[9px] font-mono text-gray-400">{day.isToday ? 'Today' : `D-${6 - idx}`}</span>
                </div>
              ))}
            </div>

            {/* Quick Action Button to verify today's business action */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1 border-t border-[#1C2427]">
              <div className="text-[11px] text-gray-400 text-center sm:text-left">
                Logging any booking, updating an invoice, or reviewing expenses automatically defends your streak.
              </div>
              <button
                onClick={handleQuickLogDailyHabit}
                className="w-full sm:w-auto px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-md cursor-pointer shrink-0"
              >
                <Flame size={14} />
                <span>Log Today's Business Action</span>
              </button>
            </div>
          </div>

          {/* Visual Rewards & Milestones Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-[#0B0F10]/70 border border-[#1E2629] space-y-3 flex flex-col justify-between">
            <div className="space-y-2.5">
              <div className="flex items-center gap-2 text-xs font-semibold text-white">
                <Trophy size={15} className="text-amber-400" />
                <span>Habit Reward Milestones</span>
              </div>

              <div className="space-y-2">
                {/* 3 Day Milestone */}
                <div className={`p-2.5 rounded-xl border flex items-center justify-between text-xs ${
                  streakData.streakDays >= 3 
                    ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' 
                    : 'bg-[#121719] border-[#1C2427] text-gray-400'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">🔥</span>
                    <span className="font-medium">3-Day Habit Lock</span>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                    streakData.streakDays >= 3 ? 'bg-amber-500/20 text-amber-300' : 'bg-gray-800 text-gray-400'
                  }`}>
                    {streakData.streakDays >= 3 ? 'UNLOCKED' : `${3 - streakData.streakDays}d left`}
                  </span>
                </div>

                {/* 7 Day Milestone */}
                <div className={`p-2.5 rounded-xl border flex items-center justify-between text-xs ${
                  streakData.streakDays >= 7 
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' 
                    : 'bg-[#121719] border-[#1C2427] text-gray-400'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">⚡</span>
                    <span className="font-medium">7-Day Profit Fortress</span>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                    streakData.streakDays >= 7 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-gray-800 text-gray-400'
                  }`}>
                    {streakData.streakDays >= 7 ? 'UNLOCKED' : `${Math.max(0, 7 - streakData.streakDays)}d left`}
                  </span>
                </div>

                {/* 30 Day Milestone */}
                <div className={`p-2.5 rounded-xl border flex items-center justify-between text-xs ${
                  streakData.streakDays >= 30 
                    ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' 
                    : 'bg-[#121719] border-[#1C2427] text-gray-400'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">👑</span>
                    <span className="font-medium">30-Day Master Sovereign</span>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                    streakData.streakDays >= 30 ? 'bg-purple-500/20 text-purple-300' : 'bg-gray-800 text-gray-400'
                  }`}>
                    {streakData.streakDays >= 30 ? 'UNLOCKED' : `${Math.max(0, 30 - streakData.streakDays)}d left`}
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#1C2427] flex items-center justify-between text-[11px]">
              <span className="text-gray-400">Current Multiplier:</span>
              <span className="text-emerald-400 font-mono font-bold">+{(streakData.streakDays * 0.1 + 1).toFixed(1)}x Focus Bonus</span>
            </div>
          </div>
        </div>
      </div>

      {/* 4. HOW AZURAOS WORKS: 4-STEP VISUAL BLUEPRINT */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h2 className="text-xl font-display font-semibold text-white flex items-center gap-2">
              <Compass size={20} className="text-[#14B8A6]" />
              How AzuraOS Works: 4-Step Blueprint
            </h2>
            <p className="text-xs text-gray-400">
              A simplified workflow designed to give you clarity and protect your net income daily.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Step 1 */}
          <div className="p-5 rounded-2xl bg-[#131718] border border-[#1F2527] hover:border-[#14B8A6]/40 transition-all space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#14B8A6]/20 text-[#14B8A6]">STEP 1</span>
              <Layers size={18} className="text-teal-400" />
            </div>
            <h3 className="font-semibold text-white text-sm">Define Income Streams</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Create your specific business lines (e.g., Strategic Advisory, Retreats, Coffee, Retainers) on the Dashboard.
            </p>
          </div>

          {/* Step 2 */}
          <div className="p-5 rounded-2xl bg-[#131718] border border-[#1F2527] hover:border-[#14B8A6]/40 transition-all space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">STEP 2</span>
              <DollarSign size={18} className="text-blue-400" />
            </div>
            <h3 className="font-semibold text-white text-sm">Log Bookings & Expenses</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Quickly record client engagements, invoice values, software subscriptions, and operating costs with 1 click.
            </p>
          </div>

          {/* Step 3 */}
          <div className="p-5 rounded-2xl bg-[#131718] border border-[#1F2527] hover:border-[#14B8A6]/40 transition-all space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">STEP 3</span>
              <TrendingUp size={18} className="text-emerald-400" />
            </div>
            <h3 className="font-semibold text-white text-sm">Defend Real Margin</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              AzuraOS automatically calculates your real net profit margin and alerts you if expenses jeopardize your target threshold.
            </p>
          </div>

          {/* Step 4 */}
          <div className="p-5 rounded-2xl bg-[#131718] border border-[#1F2527] hover:border-[#14B8A6]/40 transition-all space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400">STEP 4</span>
              <FileText size={18} className="text-purple-400" />
            </div>
            <h3 className="font-semibold text-white text-sm">Invoicing & Tax Ready</h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              Generate one-click PDF invoices with bank transfer details and export complete financial reports for tax season.
            </p>
          </div>
        </div>
      </div>

      {/* 4. BYOK (BRING YOUR OWN KEY) GUIDE - MULTI-PROVIDER SOVEREIGNTY */}
      <div id="byok-section" className="space-y-6">
        <MultiProviderBYOKCard
          onOpenModal={(providerId) => {
            setMultiByokInitialProvider(providerId || 'gemini');
            setShowMultiByokModal(true);
          }}
          compact={false}
        />

        {/* Multi-Provider Benefits & Transparency Box */}
        <div className="rounded-3xl bg-[#131718] border border-[#1F2527] p-6 sm:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#1F2527] pb-5">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Key size={20} className="text-[#14B8A6]" />
                <h2 className="text-lg sm:text-xl font-display font-semibold text-white">
                  Why Multi-Provider Sovereign BYOK?
                </h2>
              </div>
              <p className="text-xs sm:text-sm text-gray-400">
                True freedom means you are never locked into a single AI corporation. Choose from 8 frontier AI providers or activate automatic failover cascading.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setMultiByokInitialProvider('gemini');
                setShowMultiByokModal(true);
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-black font-bold text-xs transition shadow-md cursor-pointer shrink-0"
            >
              <Key size={14} />
              <span>Configure Keys &amp; Failover</span>
            </button>
          </div>

          {/* 3 Pillars of Multi-Provider BYOK */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-2xl bg-[#0E1214] border border-[#1B2123] space-y-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center text-xs font-bold font-mono">
                $0
              </div>
              <h4 className="text-xs font-semibold text-white">100% Free Tier (Google)</h4>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Google AI Studio gives every user up to <strong>1,500 free requests per day</strong> with Gemini 3.8 Flash. Perfect for solo founders starting out.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-[#0E1214] border border-[#1B2123] space-y-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center text-xs font-bold">
                <Zap size={15} />
              </div>
              <h4 className="text-xs font-semibold text-white">Zero Platform Markup</h4>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                You pay raw wholesale API pricing directly to OpenAI, Anthropic, Groq, or DeepSeek. AzuraOS takes <strong>0% cut or surcharge</strong> on your AI tokens.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-[#0E1214] border border-[#1B2123] space-y-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center text-xs font-bold">
                <Lock size={14} />
              </div>
              <h4 className="text-xs font-semibold text-white">Client-Side Vault</h4>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                All keys are stored strictly in your local browser sandbox. They are never sent to central backend databases or shared between users.
              </p>
            </div>
          </div>

          {/* Quick Provider Matrix */}
          <div className="p-4 rounded-2xl bg-[#0B0E0F] border border-[#1C2225] space-y-3">
            <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
              <span>Supported AI Frontier Providers</span>
              <span className="text-[10px] font-mono text-teal-400 lowercase">(select any active key)</span>
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('gemini');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-teal-400 transition">Google Gemini</div>
                <div className="text-[10px] text-emerald-400 font-mono">1,500 req/day free</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('openai');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-emerald-400 transition">OpenAI</div>
                <div className="text-[10px] text-gray-400 font-mono">GPT-4o &amp; o3-mini</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('anthropic');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-amber-400 transition">Anthropic Claude</div>
                <div className="text-[10px] text-gray-400 font-mono">Claude 3.7 Sonnet</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('groq');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-orange-400 transition">Groq LPUs</div>
                <div className="text-[10px] text-orange-400 font-mono">500 tokens/sec</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('deepseek');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-cyan-400 transition">DeepSeek</div>
                <div className="text-[10px] text-cyan-400 font-mono">V3 &amp; R1 Reasoner</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('openrouter');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-indigo-400 transition">OpenRouter</div>
                <div className="text-[10px] text-indigo-400 font-mono">200+ models in 1 key</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('mistral');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-amber-400 transition">Mistral AI</div>
                <div className="text-[10px] text-amber-400 font-mono">Large &amp; Codestral</div>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMultiByokInitialProvider('perplexity');
                  setShowMultiByokModal(true);
                }}
                className="p-2.5 rounded-xl bg-[#121618] hover:bg-[#182023] border border-[#1E2528] text-left transition cursor-pointer group"
              >
                <div className="font-bold text-white group-hover:text-teal-400 transition">Perplexity AI</div>
                <div className="text-[10px] text-teal-400 font-mono">Live Sonar Search</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. ACTIONS & COPILOT PROMPT */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
        {/* Launch Dashboard */}
        <button
          onClick={() => onNavigate('/dashboard')}
          className="p-5 rounded-2xl bg-gradient-to-r from-[#14B8A6] to-[#0D9488] text-black font-bold text-sm flex items-center justify-between hover:shadow-lg hover:shadow-[#14B8A6]/20 transition-all cursor-pointer group"
        >
          <div className="text-left">
            <div className="text-[10px] uppercase font-bold text-black/70">Ready to track</div>
            <div className="text-base font-display">Enter My Dashboard</div>
          </div>
          <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Sync Google Calendar */}
        <button
          onClick={() => onNavigate('/calendar')}
          className="p-5 rounded-2xl bg-[#131718] border border-[#1F2527] hover:border-[#14B8A6]/40 text-left transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase font-bold text-gray-400">Integration</div>
            <Calendar size={18} className="text-[#14B8A6]" />
          </div>
          <div className="text-sm font-semibold text-white group-hover:text-[#14B8A6] transition-colors">
            Connect Google Calendar
          </div>
          <p className="text-[11px] text-gray-400 mt-1">2-way sync for client appointments</p>
        </button>

        {/* In-App AI Concierge */}
        <button
          onClick={() => {
            if (onOpenConcierge) {
              onOpenConcierge();
            } else {
              showToast('Click the AI Concierge badge at the bottom right anytime!');
            }
          }}
          className="p-5 rounded-2xl bg-[#131718] border border-[#1F2527] hover:border-blue-500/40 text-left transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase font-bold text-blue-400">Ask Anything</div>
            <MessageSquare size={18} className="text-blue-400" />
          </div>
          <div className="text-sm font-semibold text-white group-hover:text-blue-300 transition-colors">
            Ask AI Concierge
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Get instant in-app guidance 24/7</p>
        </button>
      </div>

      {/* Cohort Onboarding & Sublicense Voucher Modal */}
      <CohortOnboardingModal
        isOpen={isCohortModalOpen}
        onClose={() => setIsCohortModalOpen(false)}
        onActivated={(newSettings) => {
          setCohortSettings(newSettings);
          setIsCohortModalOpen(false);
          showToast(`🎓 ${newSettings.cohortName} sponsorship activated!`, 'success');
        }}
        showToast={showToast}
      />

      {/* Sovereign Multi-Provider BYOK Key Vault Modal */}
      <MultiProviderBYOKModal
        isOpen={showMultiByokModal}
        onClose={() => setShowMultiByokModal(false)}
        showToast={showToast}
        initialProvider={multiByokInitialProvider}
      />
    </div>
  );
}
